import { FormattedPercentage } from "@msys/ui";
import type { GridColDef } from "@mui/x-data-grid-premium";
import React from "react";
import type { EstimatedCalculation__ItemFragment } from "../../doc-items/boxes/EstimatedCalculationBox.generated.js";
import type { DocDataGrid_ItemFragment } from "../DocDataGrid.generated.js";
import { DataGridFloatEditCell } from "../cells/DataGridFloatEditCell.js";
import type { ModifyItemCalculationFn } from "../useModifyItemCalculation.js";
import type { DocDataGridRow } from "../types.js";
import { makeCalculationValueSetter } from "../cells/helpers.js";

interface Options<
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
> {
  headerName: string;
  canEdit: boolean;
  modifyItemCalculation: ModifyItemCalculationFn;
  itemCanBeEdited?: (item: T) => boolean;
}

export function createMaterialMarginColumn<
  T extends DocDataGrid_ItemFragment & EstimatedCalculation__ItemFragment,
>(options: Options<T>): GridColDef<DocDataGridRow<T>> {
  return {
    field: "materialMargin",
    headerName: options.headerName,
    width: 100,
    editable: options.canEdit,
    sortable: false,
    filterable: false,
    // stored as 0–1, displayed as percentage
    valueGetter: (_value, row) =>
      row.item?.proposedCalculation?.materialMargin ?? 0,
    valueSetter: makeCalculationValueSetter<T>()(
      "materialMargin",
      (itemId, value) =>
        options.modifyItemCalculation(itemId, {
          materialMargin: value,
        })
    ),
    renderCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (item.type !== "paid") return null;
      if (options.itemCanBeEdited?.(item) === false) return null;
      return <FormattedPercentage value={params.value ?? 0} />;
    },
    // multiplier=100: user types "25" → stored as 0.25
    renderEditCell: params => {
      if (params.row.kind === "input" || !params.row.item) return null;
      const item = params.row.item;
      if (item.type !== "paid") return null;
      if (options.itemCanBeEdited?.(item) === false) return null;
      return <DataGridFloatEditCell {...params} multiplier={100} unit="%" />;
    },
  };
}
